import { useEffect } from 'react';

import { SliderProductsTypeEnum } from '@/components/Cms/SliderProducts';
import type { ContentfulSliderProductsAutomaticFragment } from '@/graphql/contentful/api';
import type { ProductFilterInput } from '@/graphql/saleor/api';
import { useIsMounted } from '@/hooks/useIsMounted';
import type { SliderProductsAutomaticType } from '@/integrations/contentful';
import { AI_RECOMMENDATION_ENABLED } from '@/lib/const';
import { recentlyViewedPresenceActions } from '@/states/recentlyViewedPresenceState';

import { AutomaticCarouselDefault } from './AutomaticCarouselDefault';
import { AutomaticCarouselYouMayAlsoLike } from './AutomaticCarouselYouMayAlsoLike';

export type AutomaticCarouselOpts = {
  currentProductSku?: string;
  excludeSlug?: string;
  filter?: ProductFilterInput;
  /** Products needed before rendering. 2 = PDP behaviour; BEC-24811 passes 1. */
  minProducts?: number;
  /** Announce to the global section so it stands down. False for that section itself. */
  registersPresence?: boolean;
  sizes?: string;
  type: SliderProductsAutomaticType;
};

const DEFAULT_MIN_RECENTLY_VIEWED_PRODUCTS = 2;

export type AutomaticCarouselProps = Omit<
  ContentfulSliderProductsAutomaticFragment,
  'sys' | 'contentfulMetadata'
> &
  AutomaticCarouselOpts & { draftMode: boolean };

export const AutomaticCarousel = ({
  filter,
  minProducts = DEFAULT_MIN_RECENTLY_VIEWED_PRODUCTS,
  registersPresence = true,
  type,
  ...props
}: AutomaticCarouselProps) => {
  // Hydration guard for the localStorage-backed list. State, not a ref: a ref
  // never triggers the re-render that reveals the carousel.
  const isMounted = useIsMounted();

  const isRecentlyViewed = type === SliderProductsTypeEnum.RecentlyViewed;
  const showsRecentlyViewed =
    isRecentlyViewed &&
    isMounted &&
    !!filter?.slugs &&
    filter.slugs.length >= minProducts;

  // Announce a CMS-placed carousel so the global section stands down (BEC-24811).
  useEffect(() => {
    if (!registersPresence || !showsRecentlyViewed) return;

    recentlyViewedPresenceActions.register();
    return () => recentlyViewedPresenceActions.unregister();
  }, [registersPresence, showsRecentlyViewed]);

  // Folds in isMounted, so this covers hydration and the minimum together.
  if (isRecentlyViewed && !showsRecentlyViewed) {
    return null;
  }

  if (
    type === SliderProductsTypeEnum.CompatibleStraps &&
    (!filter?.slugs || !filter?.slugs.length)
  ) {
    return null;
  }

  const RenderComponent =
    type === SliderProductsTypeEnum.YouMayAlsoLike && AI_RECOMMENDATION_ENABLED
      ? AutomaticCarouselYouMayAlsoLike
      : AutomaticCarouselDefault;

  // Wrapper lives in the renderers, so an in-flight fetch leaves no empty section.
  return (
    <RenderComponent
      data-test-id='AutomaticCarouselRenderOthers'
      {...props}
      filter={filter}
      type={type}
    />
  );
};

AutomaticCarousel.displayName = 'AutomaticCarousel';
